import { useMemo, useState } from "react";
import { TIER_COLORS, courtTypeOf } from "../utils/tiers";
import { computeMatches } from "../utils/matchFinder";

function withAge(p) {
  return p.age != null ? `${p.name} (${p.age})` : p.name;
}

export default function FindTimeView({ players, groups, onPlayerClick, onJoinGroup, onCreateGroupWithPlayers }) {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const selectedPlayers = selectedIds
    .map(id => players.find(p => p.id === id))
    .filter(Boolean);

  const candidates = search.trim()
    ? players
        .filter(p => !selectedIds.includes(p.id) && p.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 8)
    : [];

  function addPlayer(p) {
    setSelectedIds(prev => [...prev, p.id]);
    setSearch("");
  }

  function removePlayer(id) {
    setSelectedIds(prev => prev.filter(x => x !== id));
  }

  const results = useMemo(
    () => computeMatches(selectedPlayers, groups),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, groups, players]
  );

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div>
        <h2 className="text-base font-bold text-gray-800 mb-1">Find a Time</h2>
        <p className="text-sm text-gray-500">
          Select two or more players to see which days/times they can meet, ranked by how many overlap.
        </p>
      </div>

      {/* Player picker */}
      <div>
        <input
          type="text"
          placeholder="Search players by name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        {candidates.length > 0 && (
          <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-1.5">
            {candidates.map(p => (
              <button
                key={p.id}
                onClick={() => addPlayer(p)}
                className="w-full text-left rounded px-3 py-2 text-xs hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-colors"
              >
                <span className="font-medium text-gray-800">{withAge(p)}</span>
                <span className="text-gray-400 ml-2">{p.tier}</span>
                {p.groupId && <span className="text-gray-400 ml-2">· currently {p.groupId}</span>}
              </button>
            ))}
          </div>
        )}

        {selectedPlayers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {selectedPlayers.map(p => {
              const c = TIER_COLORS[p.tier] || TIER_COLORS["No Group"];
              return (
                <span key={p.id} className={`${c.bg} ${c.border} border rounded-full pl-3 pr-1 py-1 text-xs flex items-center gap-1.5`}>
                  <button onClick={() => onPlayerClick(p)} className="font-medium text-gray-800 hover:underline">
                    {withAge(p)}
                  </button>
                  <button
                    onClick={() => removePlayer(p.id)}
                    title="Remove from selection"
                    className="text-gray-400 hover:text-red-500 leading-none text-sm"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Results */}
      {selectedPlayers.length < 2 ? (
        <p className="text-sm text-gray-400 italic">Select at least 2 players to search for a common time.</p>
      ) : results.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No overlapping availability found among the selected players.</p>
      ) : (
        <div className="space-y-2">
          {results.map(r => (
            <MatchRow
              key={r.key}
              result={r}
              totalSelected={selectedPlayers.length}
              onJoinGroup={onJoinGroup}
              onCreateGroupWithPlayers={onCreateGroupWithPlayers}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MatchRow({ result, totalSelected, onJoinGroup, onCreateGroupWithPlayers }) {
  const { day, slot, matchCount, matched, missing, openCourts, joinableGroups, suggestedTier } = result;
  const isFullMatch = matchCount === totalSelected;

  function handleCreateAt(court) {
    const [startTime] = slot.split("–");
    onCreateGroupWithPlayers(
      { tier: suggestedTier, day, startTime, duration: 1, court, courtType: courtTypeOf(court) },
      matched.map(p => p.id)
    );
  }

  return (
    <div className={`border rounded-lg p-3 ${isFullMatch ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-800">{day} · {slot}</div>
        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${isFullMatch ? "bg-green-200 text-green-800" : "bg-amber-200 text-amber-800"}`}>
          {matchCount}/{totalSelected} available
        </span>
      </div>
      <div className="text-xs text-gray-600 mt-1.5">
        <span className="font-medium text-gray-700">✓ {matched.map(withAge).join(", ")}</span>
        {missing.length > 0 && (
          <span className="text-gray-400"> · not free: {missing.map(withAge).join(", ")}</span>
        )}
      </div>

      {(joinableGroups.length > 0 || openCourts.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {joinableGroups.map(({ group, joiningPlayers }) => (
            <button
              key={group.id}
              onClick={() => onJoinGroup(group.id, joiningPlayers.map(p => p.id))}
              className="text-xs bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 rounded px-2 py-1 font-medium transition-colors"
              title={`Add ${joiningPlayers.map(withAge).join(", ")} to ${group.id}`}
            >
              Add to {group.id} · {group.tier} · {group.playerIds.length}/4
            </button>
          ))}
          {openCourts.map(court => (
            <button
              key={court}
              onClick={() => handleCreateAt(court)}
              className="text-xs bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded px-2 py-1 font-medium transition-colors"
              title={`Create a new ${suggestedTier} group for ${matched.map(withAge).join(", ")}`}
            >
              + New group · {court}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
